import { storyItems } from '@/mocks/stories';
import { castItems } from '@/mocks/cast';
import { newsItems } from '@/mocks/news';

export type ContentType = 'story' | 'cast' | 'news';

export interface MetaField {
  label: string;
  value: string;
}

export interface ContentItem {
  id: string;
  type: ContentType;
  title: string;
  subtitle: string;
  excerpt: string;
  image: string;
  date: string;
  dateLabel: string;
  weight: number;
  tags: string[];
  meta: MetaField[];
  quote: string;
  body: string[];
}

type RawItem = Omit<ContentItem, 'type'> & { type: string };

const normalize = (items: RawItem[]): ContentItem[] =>
  items.map((item) => ({ ...item, type: item.type as ContentType }));

export const allContent: ContentItem[] = [
  ...normalize(storyItems),
  ...normalize(castItems),
  ...normalize(newsItems),
];

export const typeLabels: Record<ContentType, string> = {
  story: '剧情介绍',
  cast: '演员详情',
  news: '影视资讯',
};

export const typePaths: Record<ContentType, string> = {
  story: '/story',
  cast: '/cast',
  news: '/news',
};

export const siteInfo = {
  brand: '我的朋友他的妻子',
  tagline: '一部关于三个人、七年与一句没说出口的话的剧集',
  email: 'contact@friend-wife-cinema.example',
  year: '2025',
};

export const getContentById = (id: string): ContentItem | undefined =>
  allContent.find((item) => item.id === id);

export const getByType = (type: ContentType): ContentItem[] =>
  allContent.filter((item) => item.type === type);

export const getStoriesByTime = (): ContentItem[] =>
  [...getByType('story')].sort((a, b) => a.date.localeCompare(b.date));

export const getStoriesByWeight = (): ContentItem[] =>
  [...getByType('story')].sort((a, b) => b.weight - a.weight);

export const getCastAlphabetical = (): ContentItem[] =>
  [...getByType('cast')].sort((a, b) => a.title.localeCompare(b.title, 'zh-Hans-CN'));

export const getNewsByDate = (): ContentItem[] =>
  [...getByType('news')].sort((a, b) => b.date.localeCompare(a.date));

export const searchContent = (query: string, type?: ContentType): ContentItem[] => {
  const keyword = query.trim().toLowerCase();
  const pool = type ? getByType(type) : allContent;
  if (!keyword) return [];
  return pool.filter((item) => {
    const haystack = [item.title, item.subtitle, item.excerpt, item.tags.join(' '), item.body.join(' ')]
      .join(' ')
      .toLowerCase();
    return haystack.includes(keyword);
  });
};

export const getRelated = (item: ContentItem, limit = 3): ContentItem[] => {
  const pool = getByType(item.type).filter((entry) => entry.id !== item.id);
  const scored = pool.map((entry) => {
    const overlap = entry.tags.filter((tag) => item.tags.includes(tag)).length;
    return { entry, score: overlap };
  });
  scored.sort((a, b) => b.score - a.score || b.entry.weight - a.entry.weight);
  return scored.slice(0, limit).map((entry) => entry.entry);
};

export const paginate = <T,>(items: T[], page: number, pageSize: number): T[] => {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

export const totalPages = (count: number, pageSize: number): number =>
  Math.max(1, Math.ceil(count / pageSize));